import typewriter from './typewriter'

/**
 * Create a Chord tracking client.
 *
 * @param {Object} config
 * @param {string} config.tenantId The tenant identifier for this store. Provided during provisioning.
 * @param {string} config.storeId The tenant identifier for this store. Provided during provisioning.
 * @param {string} config.domain The domain that the store is running on.
 * @param {string} config.currency The ISO 4217 currency code that this store is transacting in.
 * @param {string} config.locale The ISO 3166-1 locale representing the language this page is being shown in.
 */
export const createChordClient = (config) => {
  const meta = {
    ownership: {
      tenant_id: config.tenantId,
      store_id: config.storeId,
    },
    store: {
      domain: config.domain,
    },
    platform: {
      name: 'Shopify',
      type: 'web',
    },
    version: {
      major: 2,
      minor: 0,
      patch: 0,
    },
    i18n: {
      currency: config.currency,
      locale: config.locale,
    },
  }

  const track = (event, properties, options, callback) => {
    if (!trackingAvailable()) {
      return false
    }

    return window.analytics.track(
      event,
      { ...properties, meta },
      options,
      callback,
    )
  }

  const identify = (...args) => {
    if (!trackingAvailable()) {
      return false
    }

    return window.analytics.identify(...args)
  }

  const page = (...args) => {
    if (!trackingAvailable()) {
      return false
    }

    return window.analytics.page(...args)
  }

  const trackingAvailable = () => !!window.analytics

  return {
    /**
     * Track an event to Segment using analytics.js that is not in the tracking plan. It inlines the `meta` key that is
     * required by all events in the tracking plan.
     *
     * The signature for this function maps to `analytics.track`
     *
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
     */
    track,
    /**
     * Identify a user to Segment with known information.
     *
     * This signature for this function maps to `analytics.identify`
     *
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
     */
    identify,
    /**
     * Track a page view to Segment.
     *
     * The signature for this function maps to `analytics.page`
     *
     * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
     */
    page,
    /**
     * Function that returns whether or not tracking is available on the page.
     */
    trackingAvailable,
    /**
     * The generated tracking plan library based upon the tracking plan in your account.
     *
     * You will need to provide the `meta` property attached to the Chord client to all events in your tracking plan.
     *
     * @example
     *
     * ```javascript
     * import chord from '~/utils/chord'
     *
     * chord.typewriter.productClicked({
     *   meta: chord.meta,
     *   product_id: '123',
     *   product_image_url: 'https://placekitten.com/408/287',
     *   product_name: 'Flux Capacitor',
     *   // imagaine that the rest of the product's properties are here.
     * })
     * ```
     */
    typewriter,
    /**
     * The `meta` property that must be included with each event in the Chord tracking plan.
     */
    meta,
  }
}

const chord = createChordClient({
  // Update these values to use whatever configuration system you might be using.
  tenantId: process.env.CHORD_TENANT_ID,
  storeId: process.env.CHORD_STORE_ID,
  domain: process.env.CHORD_DOMAIN,
  currency: 'USD',
  locale: 'en-US',
})

export default chord
